import classNames from 'classnames';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { Button, Flex, Text } from '@chakra-ui/react';
import { useFormContext } from 'react-hook-form';
import { ReCAPTCHA } from 'react-google-recaptcha';
import {
  EMAIL_MARKETING_TYPE,
  NOTIFICATION_TYPE,
  NOTIFICATION_TYPE_TOPIC_MAP,
  PLATFORM,
  SUBSCRIPTION_STATUS_TYPE,
} from '../../../../../constants/benchat';
import { RECAPTCHA_TOKEN_FIELD } from '../../../../../constants/recaptchable';
import useGeneralSubscription from '../../../../../hooks/useGeneralSubscription';
import authenticationService from '../../../../../service/authenticationService';
import pnotifyService from '../../../../../service/pnotifyService';
import { CheckCircle } from '../../../../context/Chakra/Icons';
import { useAppContext } from '../../../context/appContext';
import { FIELD } from '../constants/field';

const EmailSubscription = ({ shouldCheckMemberReward }) => {
  const { t } = useTranslation();
  const { currentUser, isRecaptchaEnabled, grecaptcha, recaptchaSiteKey } =
    useAppContext();
  const userId = currentUser._id;
  const reCAPTCHARef = useRef();
  const { getValues, setValue } = useFormContext();
  const [hasSubscribed, setHasSubscribed] = useState(
    (getValues(FIELD.SUBSCRIBED_EMAIL_TYPES) || []).includes(
      EMAIL_MARKETING_TYPE,
    ),
  );
  const { subscribe, checkMemberReward } = useGeneralSubscription({
    userId,
    platform: PLATFORM.EMAIL,
    subscriptions: [],
    topics: NOTIFICATION_TYPE_TOPIC_MAP[NOTIFICATION_TYPE.MARKETING],
  });
  const handleSubscribe = async () => {
    const result = await subscribe();
    switch (result) {
      case SUBSCRIPTION_STATUS_TYPE.SUCCESS:
        setValue(FIELD.SUBSCRIBED_EMAIL_TYPES, [EMAIL_MARKETING_TYPE]);
        setHasSubscribed(true);
        if (shouldCheckMemberReward) checkMemberReward(userId);
        pnotifyService.notify(t('member_profile.subscribe.successfully'));
        break;
      case SUBSCRIPTION_STATUS_TYPE.USER_WITHOUT_EMAIL_ERROR:
        pnotifyService.notify(t('member_profile.email.subscribe.error'), {
          customClass: 'error',
          icon: 'fa fa-exclamation-triangle',
        });
        break;
      default:
        pnotifyService.notify(t('member_profile.subscribe.error'), {
          customClass: 'error',
          icon: 'fa fa-exclamation-triangle',
        });
    }
  };

  const handleUnsubscribe = async () => {
    try {
      const payload = {
        user: {
          subscribed_email_types: [],
        },
      };
      if (isRecaptchaEnabled) {
        payload[RECAPTCHA_TOKEN_FIELD] =
          await reCAPTCHARef.current?.executeAsync?.();
        reCAPTCHARef.current?.reset?.();
      }
      await authenticationService.updateUser(userId, payload);
      setValue(FIELD.SUBSCRIBED_EMAIL_TYPES, []);
      setHasSubscribed(false);
      pnotifyService.notify(t('member_profile.unsubscribe.successfully'));
    } catch (error) {
      console.error(error);
      pnotifyService.notify(t('member_profile.unsubscribe.error'), {
        customClass: 'error',
        icon: 'fa fa-exclamation-triangle',
      });
    }
  };

  return (
    <Flex justifyContent="space-between" alignItems="center" w="100%">
      <Text
        variant="formLabel"
        className="member-center-profile__label"
        textAlign="left"
      >
        {t('membership.profile.email')}
      </Text>
      {isRecaptchaEnabled && (
        <ReCAPTCHA
          ref={reCAPTCHARef}
          size="invisible"
          grecaptcha={grecaptcha}
          sitekey={recaptchaSiteKey}
        />
      )}
      <Button
        type="button"
        variant="outline"
        onClick={hasSubscribed ? handleUnsubscribe : handleSubscribe}
        data-e2e-id="member-center-profile-marketing-subscription_button--email"
        className={classNames(
          'member-center-profile__outline-button',
          hasSubscribed
            ? 'general-unsubscribe-button'
            : 'general-subscribe-button',
        )}
      >
        {hasSubscribed ? (
          <>
            <CheckCircle />
            <Text variant="subscribeText">
              {t('member_profile.subscribed')}
            </Text>
          </>
        ) : (
          <Text variant="subscribeText">{t('member_profile.subscribe')}</Text>
        )}
      </Button>
    </Flex>
  );
};

EmailSubscription.propTypes = {
  shouldCheckMemberReward: PropTypes.bool,
};

export default EmailSubscription;
